import { useResetOnKeyChange } from "@/hooks/use-reset-on-key-change";
import type { ItemImageSources } from "@/lib/catalog-item-image";

/**
 * Whether *this slot's* picture failed to load, and the note that says so.
 *
 * **One slot, one answer.** The gallery draws the same image more than once —
 * the main picture and its thumbnail, and again inside the lightbox — and each
 * of those resolves the ladder against its own `sizes`, so they can be fetching
 * genuinely different bytes: a 68px cell takes the w128 rung, the main slot
 * takes w512, and the lightbox — on the ladder itself since #499 — takes w1024
 * or w2048. Tracking the failure against the image's **id** made those slots
 * share a verdict, so a missing thumbnail rendition blanked a main picture that
 * had loaded perfectly well. The failure belongs to what was fetched, not to the
 * row it came from.
 *
 * **Keyed on the URLs, so a retry is possible and a stale verdict is not.** A
 * failed fetch is remembered only for as long as the slot is asking for the same
 * bytes: point it at a different image, or at the same row once its renditions
 * exist, and it tries again. Remembering "this row failed" across a change of
 * URL is how an image that has since been generated stays broken on screen, and
 * remembering it forever is how A -> B -> A never re-attempts A.
 *
 * **This hook hears about a failure only if the `error` event reaches it, and
 * that is not free under SSR.** A server-rendered `<img>` can finish failing
 * before React hydrates and attaches `onError`; the event does not replay, so
 * the verdict is lost and the browser's broken-image glyph stays up (#539).
 * `rearmImageIfAlreadyFailed` (`@/lib/rearm-image`) closes that window, wired as
 * the `<img>`'s `ref` inside `ItemPicture` and in the one slot that does not go
 * through it (`image-upload-field.tsx`'s bare `<img>`) — so nothing here
 * changed, and callers of this hook need do nothing.
 *
 * **Do not "fix" that by judging the element at mount instead.** Reading
 * `complete && naturalWidth === 0` in a ref or an effect and calling
 * `setFailed(true)` is the shape that keeps suggesting itself; it was written
 * once and reverted, for two independent reasons. A *freshly created* `<img>`
 * reports exactly that state — `complete` is true while `src` and `srcset` are
 * both absent, and `naturalWidth` is 0 before any fetch — so the reading cannot
 * distinguish "failed" from "not begun". And on a settled element it can still
 * describe the *previous* attempt, which re-asserts a stale verdict and breaks
 * the retry this hook exists to allow: four specs in
 * `catalog/item-image.browser.test.tsx` failed on that revert. Re-arming the
 * fetch states no verdict at all, which is why it is the shape that works.
 *
 * There is no matching donor behaviour to hold this to: `ImagePreview.tsx` has
 * no `onError` at all. It does not need one — modsy's image host answers a
 * missing object with a 403 whose body is a decodable placeholder PNG, so over
 * there the `<img>` loads and the fallback never runs in the client (measured on
 * the running stack, #496). Atlas serves its own objects, where a missing one is
 * a real failure, so the placeholder is drawn here instead; which placeholder is
 * spec-added per slot (#303 AC 2, #269's "the no-image and fetch-error
 * fallback").
 */
export function useItemImageFailure(sources: ItemImageSources): [boolean, () => void] {
  const key = [sources.src, ...sources.sources.map((s) => `${s.type} ${s.srcSet}`)].join("\n");
  const [failed, setFailed] = useResetOnKeyChange(key, false);
  return [failed, () => setFailed(true)];
}
